// ITAM-2622 (3.9) — error mapping for the adviser profile.
//
// Backend 77a860bd returns a STRUCTURED body for pharmacist failures —
// `400 {errorCode, message}` from GlobalExceptionHandler, with the codes in
// PharmacistErrorCode — and moved "the profile row is missing" from a `false`
// return (which the controller turned into 400) to EntityNotFoundException,
// which maps to 404, on both the profile and the working-hours update.
//
// Recovery stays refetch-first for the ambiguous cases: a 400 we hold no code
// for cannot distinguish "already exists" from a validation or upload failure,
// and a 404 means the row is gone — re-reading the truth is the honest next
// step for either. PHARMACIST_ASSOCIATION_REQUIRED is the one code the UI
// branches on, because it names a condition retrying can never clear.

/** The shape this codebase's axios rejections actually have. */
type RejectionLike = {
  response?: { status?: number; data?: { errorCode?: string } };
};

const rejection = (error: unknown): RejectionLike =>
  typeof error === "object" && error !== null ? (error as RejectionLike) : {};

const status = (error: unknown): number | undefined =>
  rejection(error).response?.status;

const errorCode = (error: unknown): string | undefined =>
  rejection(error).response?.data?.errorCode;

export const AVAILABILITY_NO_PROFILE =
  "Prvo kreirajte profil savetnika.";
export const GENERIC_SAVE_ERROR =
  "Čuvanje nije uspelo. Pokušajte ponovo, a ako se ponovi, obratite se podršci.";
export const GENERIC_LOAD_ERROR =
  "Učitavanje nije uspelo. Pokušajte ponovo.";
export const NO_PHARMACY_NOTICE =
  "Nalog nije povezan sa apotekom — aktivacija dostupnosti nije moguća dok administrator ne završi povezivanje.";
export const PROFILE_GONE_ERROR =
  "Profil više ne postoji. Kreirajte ga ponovo.";

/** Backend `PharmacistErrorCode.PHARMACIST_ASSOCIATION_REQUIRED`, always a 400. */
export const isAssociationRequired = (error: unknown): boolean =>
  errorCode(error) === "PHARMACIST_ASSOCIATION_REQUIRED";

export const mapAvailabilityError = (error: unknown): string => {
  if (isAssociationRequired(error)) {
    // Only an administrator can clear this, so the generic "try again" copy
    // would be instructing the pharmacist to do the one thing that cannot work.
    return NO_PHARMACY_NOTICE;
  }
  if (status(error) === 404) {
    // The availability endpoint needs an existing profile row.
    return AVAILABILITY_NO_PROFILE;
  }
  return GENERIC_SAVE_ERROR;
};

/**
 * True when the caller must REFETCH before rendering anything else: a 400 tells
 * us nothing about what the server did, and a 404 says the row is gone. Both
 * are answered by re-reading the truth.
 */
export const shouldRefetchAfterError = (error: unknown): boolean => {
  const code = status(error);
  return code === 400 || code === 404;
};

/**
 * Deliberately has NO 404 branch. A 404 only tells us the save failed, not what
 * the server holds now — the caller decides, after its reconciliation GET, and
 * uses PROFILE_GONE_ERROR only once absence is a confirmed fact. Returning it
 * from here would name the create form even when the GET failed and the editor
 * is still the only thing on screen.
 */
export const mapProfileSaveError = (error: unknown): string => {
  if (status(error) === 400) {
    return "Podaci nisu prihvaćeni. Proverite unos i pokušajte ponovo.";
  }
  return GENERIC_SAVE_ERROR;
};

/**
 * Like `mapProfileSaveError`, deliberately has NO 404 branch, for the same
 * reason: only the reconciliation GET distinguishes "the row is gone" — where
 * the page is now the create form and the caller sets PROFILE_GONE_ERROR — from
 * "we could not find out", where the hours editor is still on screen and
 * create-the-profile copy would name an action that is not rendered.
 */
export const mapWorkingHoursError = (error: unknown): string => {
  if (status(error) === 400) {
    return "Radno vreme nije prihvaćeno. Proverite unos i pokušajte ponovo.";
  }
  return GENERIC_SAVE_ERROR;
};
